/**
 * Task favourites — saved dispatch templates for one-click re-run.
 * Mirrors `useTasks.ts`'s `useDispatchTaskV1` pattern (site-scoped
 * TanStack Query mutations against `v1-client.ts`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { favoritesKey } from '../scope';
import { tasksQueryOptions } from '../query-options';
import { createFavorite, deleteFavorite, executeFavorite, fetchFavorites } from '../v1-client';
import type {
  CreateTaskFavoriteRequest,
  DispatchTaskResponse,
  SiteId,
  TaskFavorite,
} from '../v1';

export function useFavoritesV1() {
  const siteId = useSafeSiteId();
  return useQuery<TaskFavorite[]>({
    queryKey: favoritesKey(siteId),
    queryFn: () => fetchFavorites(siteId as SiteId),
    enabled: Boolean(siteId),
  });
}

export function useCreateFavoriteV1() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<TaskFavorite, Error, CreateTaskFavoriteRequest>({
    mutationFn: (body) => createFavorite(siteId as SiteId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: favoritesKey(siteId) }),
  });
}

export function useDeleteFavoriteV1() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<void, Error, string>({
    mutationFn: (favoriteId) => deleteFavorite(siteId as SiteId, favoriteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: favoritesKey(siteId) }),
  });
}

export function useExecuteFavoriteV1() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<DispatchTaskResponse, Error, string>({
    mutationFn: (favoriteId) =>
      executeFavorite(siteId as SiteId, favoriteId, {
        idempotency_key: crypto.randomUUID(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tasksQueryOptions(siteId ? (siteId as SiteId) : null).queryKey }),
  });
}
